import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

gsap?.registerPlugin(ScrollTrigger);

const ContactSection = () => {
  const [language, setLanguage] = useState('en');
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      cardsRef?.current?.forEach((card, index) => {
        if (card) {
          gsap?.fromTo(
            card,
            { y: 50, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              delay: index * 0.2,
              scrollTrigger: {
                trigger: card,
                start: 'top 80%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx?.revert();
  }, []);

  const content = {
    en: {
      title: 'Ready to Transform Your Business?',
      subtitle:
        'Get in touch with our team to start your journey to seamless booking automation',
      contactMethods: [
        {
          icon: 'Phone',
          title: 'Call Us',
          description: 'Speak directly with our sales team',
          contact: '+1 (555) 123-4567',
          action: 'Call Now',
          available: 'Mon-Fri, 9 AM - 6 PM EST',
        },
        {
          icon: 'Mail',
          title: 'Email Sales',
          description: 'Get detailed information and pricing',
          contact: 'sales@slotyfi.com',
          action: 'Send Email',
          available: 'Response within 2 hours',
        },
        {
          icon: 'MessageCircle',
          title: 'Live Chat',
          description: 'Instant answers to your questions',
          contact: 'Available on our website',
          action: 'Start Chat',
          available: '24/7 Support Available',
        },
        {
          icon: 'Calendar',
          title: 'Schedule Meeting',
          description: 'Book a consultation with our experts',
          contact: 'Choose your preferred time',
          action: 'Book Meeting',
          available: 'Flexible scheduling',
        },
      ],
      socialProof: {
        title: 'Join 2,847+ Businesses Already Using Slotyfi',
        metrics: [
          { value: '2,847+', label: 'Active Businesses' },
          { value: '156,000+', label: 'Appointments Managed' },
          { value: '85%', label: 'Average No-Show Reduction' },
          { value: '4.9/5', label: 'Customer Satisfaction' },
        ],
      },
      urgency: {
        title: 'Limited Time Offer',
        subtitle: 'Get 3 months free when you sign up this month',
        cta: 'Claim Your Offer',
        expires: 'Offer expires November 30, 2025',
      },
    },
    fa: {
      title: 'آماده تغییر کسب‌وکارتان هستید؟',
      subtitle:
        'با تیم ما تماس بگیرید تا سفر خود را به سمت خودکارسازی یکپارچه رزرو شروع کنید',
      contactMethods: [
        {
          icon: 'Phone',
          title: 'تماس تلفنی',
          description: 'مستقیماً با تیم فروش ما صحبت کنید',
          contact: '+1 (555) 123-4567',
          action: 'تماس بگیرید',
          available: 'دوشنبه-جمعه، 9 صبح - 6 عصر EST',
        },
        {
          icon: 'Mail',
          title: 'ایمیل فروش',
          description: 'اطلاعات تفصیلی و قیمت‌گذاری دریافت کنید',
          contact: 'sales@slotyfi.com',
          action: 'ایمیل بفرستید',
          available: 'پاسخ ظرف 2 ساعت',
        },
        {
          icon: 'MessageCircle',
          title: 'چت زنده',
          description: 'پاسخ فوری به سوالات شما',
          contact: 'در وب‌سایت ما موجود است',
          action: 'شروع چت',
          available: 'پشتیبانی 24/7 موجود',
        },
        {
          icon: 'Calendar',
          title: 'برنامه‌ریزی جلسه',
          description: 'مشاوره با متخصصان ما رزرو کنید',
          contact: 'زمان ترجیحی خود را انتخاب کنید',
          action: 'رزرو جلسه',
          available: 'برنامه‌ریزی انعطاف‌پذیر',
        },
      ],
      socialProof: {
        title: 'به 2,847+ کسب‌وکاری که از اسلاتیفای استفاده می‌کنند بپیوندید',
        metrics: [
          { value: '2,847+', label: 'کسب‌وکار فعال' },
          { value: '156,000+', label: 'قرارملاقات مدیریت شده' },
          { value: '85%', label: 'متوسط کاهش غیبت' },
          { value: '4.9/5', label: 'رضایت مشتری' },
        ],
      },
      urgency: {
        title: 'پیشنهاد محدود',
        subtitle: 'با ثبت‌نام در این ماه 3 ماه رایگان دریافت کنید',
        cta: 'پیشنهاد خود را دریافت کنید',
        expires: 'پیشنهاد تا 30 نوامبر 2025 منقضی می‌شود',
      },
    },
  };

  const currentContent = content?.[language];

  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 ${language === 'fa' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {currentContent?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {currentContent?.subtitle}
          </p>
        </div>

        {/* Contact Methods */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {currentContent?.contactMethods?.map((method, index) => (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className="bg-gray-50 rounded-2xl p-8 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name={method?.icon} size={32} color="#2563EB" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {method?.title}
              </h3>
              <p className="text-gray-600 mb-4">{method?.description}</p>

              <div className="mb-4">
                <div className="font-semibold text-gray-900 mb-1">
                  {method?.contact}
                </div>
                <div className="text-sm text-gray-500">{method?.available}</div>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                iconName={method?.icon}
                iconPosition="left">
                {method?.action}
              </Button>
            </div>
          ))}
        </div>

        {/* Social Proof Metrics */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 mb-16 text-white text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-8">
            {currentContent?.socialProof?.title}
          </h3>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {currentContent?.socialProof?.metrics?.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold mb-2">
                  {metric?.value}
                </div>
                <div className="text-blue-100">{metric?.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Urgency CTA */}
        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl p-12 text-center border-2 border-orange-200">
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Icon name="Clock" size={16} className="mr-2" />
              {currentContent?.urgency?.title}
            </div>

            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              {currentContent?.urgency?.subtitle}
            </h3>

            <p className="text-gray-600 mb-8">
              {currentContent?.urgency?.expires}
            </p>

            <Button
              variant="default"
              size="lg"
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl"
              iconName="Gift"
              iconPosition="left">
              {currentContent?.urgency?.cta}
            </Button>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-8 text-lg">
            {language === 'en'
              ? "Still have questions? We're here to help you make the right decision."
              : 'هنوز سوال دارید؟ ما اینجا هستیم تا به شما کمک کنیم تصمیم درستی بگیرید.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              iconName="Calendar"
              iconPosition="left">
              {language === 'en' ? 'Book Free Demo' : 'رزرو دمو رایگان'}
            </Button>

            <Button
              variant="outline"
              size="lg"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50"
              iconName="Phone"
              iconPosition="left">
              {language === 'en' ? 'Call Sales Team' : 'تماس با تیم فروش'}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
